import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Image,
  ActivityIndicator,
} from 'react-native';
import { useNavigation, useTheme, useRoute } from '@react-navigation/native';
import { Icon as CustomIcon } from '@components';
import { Theme } from '@theme';
import { Contact, useGetColorLabelById, usePaginateColorLabelContacts, useScreen } from '@hooks';
import { SafeAreaView } from 'react-native-safe-area-context';
import { defaultImage } from '@assets';
import { useLanguage } from '@context';

type RelationalColorDetailsProps = {
  relationalColorId?: string;
  onEdit?: () => void;
};

export const RelationalColorDetails: React.FC<RelationalColorDetailsProps> = ({ relationalColorId: propRelationalColorId, onEdit }) => {
  const navigation = useNavigation<any>();
  const route = useRoute<any>();
  const theme = useTheme();
  const styles = getStyles(theme);
  const { t } = useLanguage();
  const {isMobile} = useScreen();
  
  const relationalColorId = propRelationalColorId || route.params?.relationalColorId;
  const { data: relationalColor, isFetched } = useGetColorLabelById(relationalColorId);
  const { flatData: colorLabelContacts } = usePaginateColorLabelContacts(relationalColor?.id ?? '');
  if (!relationalColor) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
            <CustomIcon name="arrowBack" size={20} color={theme.colors.primary} />
          </TouchableOpacity>
        </View>
        <View style={styles.errorContainer}>
          {!isFetched && <ActivityIndicator size="large" color={theme.colors.primary} />}
          {isFetched && <Text style={styles.errorText}>{t('relationalColorsScreen.notFound')}</Text>}
        </View>
      </SafeAreaView>
    );
  }

  const handleEdit = () => {
    if(onEdit) {
      onEdit();
    } else {
      navigation.navigate('EditRelationalColor', { relationalColorId: relationalColor.id });
    }
  };

  const renderContactItem = (contact: Contact) => (
    <TouchableOpacity key={contact.id} style={styles.contactItem} onPress={() => (navigation as any).navigate('Profile', {
      screen: 'DetailsGhostProfile',
      params: { profileId: contact.id }
    })}>
      <Image source={!contact.avatarUrl? defaultImage : { uri: contact.avatarUrl}} style={styles.contactAvatar} />
      <Text style={styles.contactName}>{contact.fullName}</Text>
    </TouchableOpacity>
  );

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        {isMobile && <TouchableOpacity style={styles.backButton} onPress={() => navigation.goBack()}>
          <CustomIcon name="arrowBack" size={20} color={theme.colors.black} />
        </TouchableOpacity>}
        <View style={styles.titleContainer}>
          <View style={[styles.color, {backgroundColor: relationalColor?.color}]}/>
          <Text style={styles.hashtagTitle}>{relationalColor.name}</Text>
        </View>
        <TouchableOpacity style={styles.editButton} onPress={handleEdit}>
          <CustomIcon name="edit" size={20} color={theme.colors.primary} />
        </TouchableOpacity>
      </View>
      <Text style={styles.sectionTitle}>{t('common.personCount', { count: colorLabelContacts.length })}</Text>
      <ScrollView style={styles.contactsList} showsVerticalScrollIndicator={!isMobile}>
        {colorLabelContacts.map(renderContactItem)}
      </ScrollView>
    </SafeAreaView>
  );
};

const getStyles = (theme: Theme) => StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#fff',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 16,
    paddingVertical: 12,
    backgroundColor: 'white',
    borderBottomWidth: 0.5,
    borderBottomColor: '#C7C7CC',
  },
  color: {
    width: 25, height: 25, borderRadius: 13
  },
  backButton: {
    padding: 8,
  },
  editButton: {
    padding: 8,
  },
  titleContainer: {
    flexDirection: 'row',
    gap: 10,
    backgroundColor: 'white',
    alignItems: 'center',
  },
  hashtagTitle: {
    fontSize: 24,
    fontWeight: '600',
    color: '#000',
  },
  sectionTitle: {
    fontSize: 13,
    color: '#8E8E93',
    fontWeight: '400',
    paddingHorizontal: 16,
    paddingTop: 16,
    paddingBottom: 8,
  },
  contactsList: {
    flex: 1,
  },
  contactItem: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: 'transparent',
    paddingHorizontal: 16,
    paddingVertical: 12,
    borderBottomWidth: 0.5,
    borderBottomColor: '#E5E5EA',
  },
  contactAvatar: {
    width: 40,
    height: 40,
    borderRadius: 20,
    marginRight: 12,
  },
  contactName: {
    flex: 1,
    fontSize: 16,
    color: '#000',
  },
  errorContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
  },
  errorText: {
    fontSize: 16,
    color: '#8E8E93',
  },
});

export default RelationalColorDetails; 